import React, { useState, useMemo, useEffect } from 'react';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import PullToRefresh from '@/components/shared/PullToRefresh';
import { useTodos } from '@/hooks/useTodos';
import { useAuth } from '@/hooks/useAuth';
import { useAppContext } from '@/context/AppContext';
import { formatDate, toISODate } from '@/utils/formatters';
import { USERS, getDisplayName } from '@/constants';
const USER_LIST = Object.values(USERS);

const EMPTY_FORM = {
  text:       '',
  targetDate: toISODate(),
  assignedTo: USER_LIST[0]?.email ?? '',
};

/** Modal to enter a mandatory completion comment before marking done */
function CompleteCommentModal({ todo, onConfirm, onClose }) {
  const [comment, setComment] = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  async function handleConfirm() {
    if (!comment.trim()) { setError('Completion comment is required'); return; }
    setError('');
    setSaving(true);
    try {
      await onConfirm(todo.id, comment.trim());
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl p-5 space-y-4 slide-up">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-bold text-gray-800">Complete Task</h2>
          <button onClick={onClose} className="text-gray-400 text-lg" aria-label="Close">✕</button>
        </div>

        <p className="text-sm text-gray-600 bg-gray-50 rounded p-2 line-clamp-2">{todo.text}</p>

        {error && <p className="text-xs text-danger bg-red-50 rounded p-2">• {error}</p>}

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
            Completion Comment <span className="text-danger">*</span>
          </label>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Describe what was done…"
            rows={3}
            className="input-field resize-none"
            aria-required="true"
            autoFocus
          />
        </div>

        <button
          onClick={handleConfirm}
          disabled={saving}
          className="btn-primary w-full py-3 disabled:opacity-60"
        >
          {saving ? 'Saving…' : '✓ Mark as Done'}
        </button>
      </div>
    </div>
  );
}


function AddTodoModal({ onSave, onClose, initialData = null }) {
  const { user } = useAuth();
  const isEdit   = !!initialData;
  const [form, setForm] = useState(initialData ? {
    text:       initialData.text,
    targetDate: initialData.targetDate,
    assignedTo: initialData.assignedTo,
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  function set(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.text.trim()) { setError('Task description is required'); return; }
    if (!form.targetDate)  { setError('Target date is required'); return; }
    if (!form.assignedTo)  { setError('Assigned to is required'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({ ...form, text: form.text.trim() });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl p-5 space-y-4 slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-bold text-gray-800">{isEdit ? 'Edit Task' : 'Add New Task'}</h2>
          <button onClick={onClose} className="text-gray-400 text-lg" aria-label="Close">✕</button>
        </div>

        {error && <p className="text-xs text-danger bg-red-50 rounded p-2">• {error}</p>}

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Task *</label>
          <textarea
            value={form.text}
            onChange={e => set('text', e.target.value)}
            placeholder="Describe the task…"
            rows={3}
            className="input-field resize-none"
            aria-required="true"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Target Date *</label>
          <input
            type="date"
            value={form.targetDate}
            onChange={e => set('targetDate', e.target.value)}
            className="input-field"
            aria-required="true"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Assigned To *</label>
          <div className="flex gap-2">
            {USER_LIST.map(u => (
              <button
                key={u.email}
                type="button"
                onClick={() => set('assignedTo', u.email)}
                className={`chip ${form.assignedTo === u.email ? 'chip-selected' : ''}`}
              >
                {u.name}
              </button>
            ))}
          </div>
        </div>

        {!isEdit && (
          <p className="text-xs text-gray-400">
            Created by: <strong>{getDisplayName(user?.email)}</strong>
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary w-full py-3 disabled:opacity-60"
        >
          {saving ? 'Saving…' : `✓ ${isEdit ? 'Update' : 'Add'} Task`}
        </button>
      </div>
    </div>
  );
}

export default function TodoScreen() {
  const { todos, loading, addTodo, updateTodo, completeTodo, reopenTodo, deleteTodo, refetch } = useTodos();
  const { clearTodoNotifications } = useAppContext();

  // Clear notifications when the screen opens
  useEffect(() => { clearTodoNotifications(); }, [clearTodoNotifications]);

  const [showAdd, setShowAdd]       = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [completeItem, setCompleteItem] = useState(null);

  // Filters
  const [search, setSearch]             = useState('');
  const [filterUser, setFilterUser]     = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');

  // Sort
  const [sortField, setSortField] = useState('targetDate');
  const [sortDir, setSortDir]     = useState('asc');

  const displayed = useMemo(() => {
    let list = [...todos];

    if (filterStatus !== 'all') {
      list = list.filter(t => t.status === filterStatus);
    }
    if (filterUser) {
      list = list.filter(t => t.assignedTo === filterUser);
    }
    if (search) {
      const term = search.toLowerCase();
      list = list.filter(t => t.text.toLowerCase().includes(term));
    }
    if (dateFrom) list = list.filter(t => t.targetDate >= dateFrom);
    if (dateTo)   list = list.filter(t => t.targetDate <= dateTo);

    list.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'targetDate') {
        cmp = (a.targetDate ?? '').localeCompare(b.targetDate ?? '');
      } else if (sortField === 'assignedTo') {
        cmp = getDisplayName(a.assignedTo).localeCompare(getDisplayName(b.assignedTo));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [todos, filterStatus, filterUser, search, dateFrom, dateTo, sortField, sortDir]);

  const pendingCount = todos.filter(t => t.status === 'pending').length;

  return (
    <div className="flex flex-col h-full">
      <Header
        title="✅ To-Dos"
        bgColor="bg-purple"
        subtitle={`${pendingCount} pending task${pendingCount !== 1 ? 's' : ''}`}
        rightActions={
          <button onClick={() => setShowAdd(true)} className="text-white text-xl font-bold" aria-label="Add task">＋</button>
        }
      />

      {/* Filters */}
      <div className="bg-white border-b border-gray-100 px-3 py-2 space-y-2 overflow-y-auto max-h-64">
        {/* Status tabs */}
        <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-lg text-xs">
          {[['pending', 'Pending'], ['done', 'Completed'], ['all', 'All']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilterStatus(val)}
              className={`flex-1 py-1 rounded-md font-semibold transition-colors ${filterStatus === val ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}
            >{label}</button>
          ))}
        </div>

        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search tasks…"
          className="input-field w-full py-1.5 text-xs"
          aria-label="Search tasks"
        />

        {/* User filter */}
        <select
          value={filterUser}
          onChange={e => setFilterUser(e.target.value)}
          className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white w-full"
          aria-label="Filter by assigned user"
        >
          <option value="">All Users</option>
          {USER_LIST.map(u => <option key={u.email} value={u.email}>{u.name}</option>)}
        </select>

        {/* Date range */}
        <div className="flex gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="input-field flex-1 py-1.5 text-xs" aria-label="From date" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="input-field flex-1 py-1.5 text-xs" aria-label="To date" />
        </div>

        {/* Sort */}
        <div className="flex gap-2 items-center">
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide flex-shrink-0">Sort:</span>
          <select value={sortField} onChange={e => setSortField(e.target.value)}
            className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white flex-1">
            <option value="targetDate">Target Date</option>
            <option value="assignedTo">Assigned To</option>
          </select>
          <button
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white whitespace-nowrap"
            aria-label={`Toggle sort direction, currently ${sortDir}`}
          >
            {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
        </div>
      </div>

      <PullToRefresh onRefresh={refetch} className="flex-1 overflow-y-auto scrollbar-none pb-20">
        {loading ? (
          <LoadingSpinner />
        ) : displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <span className="text-4xl">✅</span>
            <p className="text-gray-400 text-sm">No tasks found</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {displayed.map(todo => (
              <TodoCard
                key={todo.id}
                todo={todo}
                onComplete={setCompleteItem}
                onReopen={reopenTodo}
                onEdit={setEditItem}
                onDelete={deleteTodo}
              />
            ))}
          </div>
        )}
      </PullToRefresh>

      <button className="fab" style={{ background: '#6A1B9A' }} onClick={() => setShowAdd(true)} aria-label="Add new task">＋</button>

      {showAdd && (
        <AddTodoModal
          onSave={async (data) => { await addTodo(data); setShowAdd(false); }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {editItem && (
        <AddTodoModal
          initialData={editItem}
          onSave={async (data) => { await updateTodo(editItem.id, data); setEditItem(null); }}
          onClose={() => setEditItem(null)}
        />
      )}

      {completeItem && (
        <CompleteCommentModal
          todo={completeItem}
          onConfirm={async (id, comment) => { await completeTodo(id, comment); setCompleteItem(null); }}
          onClose={() => setCompleteItem(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}

function TodoCard({ todo, onComplete, onReopen, onEdit, onDelete }) {
  const isDone      = todo.status === 'done';
  const assignedName = getDisplayName(todo.assignedTo);
  const createdName  = getDisplayName(todo.createdBy);

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = !isDone && todo.targetDate && todo.targetDate < today;

  return (
    <div className={`card border-l-4 ${isDone ? 'border-success opacity-70' : isOverdue ? 'border-danger' : 'border-purple'}`}>
      <div className="flex items-start gap-3">
        {/* Done checkbox */}
        <button
          onClick={() => isDone ? onReopen(todo.id) : onComplete(todo)}
          className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
            isDone ? 'bg-success border-success text-white' : 'border-gray-300 hover:border-success'
          }`}
          aria-label={isDone ? 'Mark as pending' : 'Mark as done'}
        >
          {isDone && <span className="text-xs">✓</span>}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${isDone ? 'line-through text-gray-400' : 'text-gray-800'}`}>
            {todo.text}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            <span className={`text-xs ${isOverdue ? 'text-danger font-semibold' : 'text-gray-400'}`}>
              🗓 {formatDate(todo.targetDate)}{isOverdue ? ' (overdue)' : ''}
            </span>
            <span className="text-xs text-gray-400">👤 {assignedName}</span>
            <span className="text-xs text-gray-400">by {createdName}</span>
          </div>
          {isDone && todo.completedAt && (
            <p className="text-xs text-success mt-0.5">Completed {formatDate(todo.completedAt.slice(0, 10))}</p>
          )}
          {isDone && todo.completionComment && (
            <p className="text-xs text-gray-500 mt-1 italic bg-gray-50 rounded px-2 py-1">
              💬 {todo.completionComment}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          {!isDone && (
            <button
              onClick={() => onEdit(todo)}
              className="text-xs text-gray-400 hover:text-primary transition-colors"
              aria-label="Edit task"
            >Edit</button>
          )}
          <button
            onClick={() => { if (confirm('Delete this task?')) onDelete(todo.id); }}
            className="text-xs text-gray-300 hover:text-danger transition-colors"
            aria-label="Delete task"
          >Delete</button>
        </div>
      </div>
    </div>
  );
}
